import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { createChartTemplatesApi } from "./chartTemplates.api";
import { useTemplateEditing } from "@/composables/useTemplateEditing";
import {
  ALL_CHART_TYPES,
  PIE_TYPES,
  CLUSTERED_TYPES,
  STACKED_TYPES,
  BASE_TYPE_LABELS,
  getBaseTypeLabel,
  isPieType,
  isClusteredType,
  isNpsTemplate,
  isNpsCard,
  isCrosstabCompatible,
  getCompatibleBaseTypes,
} from "./chartTemplates.constants";
import { captureEvent } from "@/lib/analytics";

// Re-export constants and helpers for backward compatibility
export {
  ALL_CHART_TYPES,
  PIE_TYPES,
  CLUSTERED_TYPES,
  STACKED_TYPES,
  BASE_TYPE_LABELS,
  getBaseTypeLabel,
  isPieType,
  isClusteredType,
  isNpsTemplate,
  isNpsCard,
};

export const useChartTemplatesStore = defineStore("chartTemplates", () => {
  // Server state (from API)
  const serverTemplates = ref([]);
  const baseTypes = ref([]);
  const loading = ref(false);
  const error = ref(null);

  // Theme context (uses token, not ID)
  const currentThemeToken = ref(null);

  // All charts from PPTX source (for source picker)
  const allCharts = ref([]);

  // Currently selected template
  const currentChartTemplateId = ref(null);

  // Use shared template editing composable
  const templateEditing = useTemplateEditing({ serverTemplates });

  function getBasePath() {
    return currentThemeToken.value ? `/master_reports/${currentThemeToken.value}/chart_templates` : "/chart_templates";
  }

  // Initialize API functions
  const api = createChartTemplatesApi(getBasePath, serverTemplates);

  function setThemeToken(themeToken) {
    currentThemeToken.value = themeToken;
  }

  // ============================================================================
  // COMPUTED
  // ============================================================================

  // Effective templates list (server + pending new - pending deletions)
  const chartTemplates = computed(() => {
    const existing = serverTemplates.value.filter((t) => !templateEditing.pendingDeletions.value.has(t.id));
    const result = [...existing];

    for (const pending of templateEditing.pendingNewTemplates.value) {
      if (pending._insertAfter !== undefined && pending._insertAfter !== null) {
        const insertIndex = result.findIndex((t) => t.id === pending._insertAfter) + 1;
        result.splice(insertIndex, 0, pending);
      } else {
        result.push(pending);
      }
    }

    return result;
  });

  // Currently selected template
  const currentChartTemplate = computed(() => {
    if (currentChartTemplateId.value === null) return null;
    return chartTemplates.value.find((t) => t.id === currentChartTemplateId.value);
  });

  // Check if there are unsaved changes (delegate to composable)
  const hasChanges = templateEditing.hasChanges;

  // ============================================================================
  // ACTIONS - FETCH
  // ============================================================================

  // surveyToken puts the request in the report-rendering context (survey-gated,
  // scoped to the survey account). Without it, it's a super_admin management call.
  async function fetchTemplates(themeToken = null, surveyToken = null) {
    if (themeToken) setThemeToken(themeToken);
    loading.value = true;
    error.value = null;

    const result = await api.fetchTemplates(surveyToken);

    if (result.success) {
      serverTemplates.value = result.templates;
      // Clear pending state on fresh fetch
      templateEditing.clearEditState();
    } else {
      error.value = result.error;
    }

    loading.value = false;
  }

  async function fetchBaseTypes() {
    const result = await api.fetchBaseTypes();

    if (result.success) {
      baseTypes.value = result.baseTypes;
    }

    return baseTypes.value;
  }

  // Load all charts from PPTX source (for source picker)
  async function loadAllCharts(pptxSourceId) {
    if (!pptxSourceId || !currentThemeToken.value) return;

    loading.value = true;
    error.value = null;

    try {
      const result = await api.loadAllCharts(currentThemeToken.value, pptxSourceId);

      if (result.success) {
        allCharts.value = result.charts;
      } else {
        error.value = result.error;
      }
    } catch (e) {
      error.value = e.message || "Failed to load charts";
    } finally {
      loading.value = false;
    }
  }

  // ============================================================================
  // ACTIONS - LOCAL EDIT
  // ============================================================================

  function selectTemplate(templateId) {
    currentChartTemplateId.value = templateId;
  }

  // Get template data with pending edits applied
  function getEffectiveTemplateData(templateId) {
    return templateEditing.getEffectiveTemplateData(chartTemplates.value, templateId);
  }

  // Update template locally (without saving to API)
  function updateLocalTemplate(templateId, field, value) {
    templateEditing.updateLocalTemplate(templateId, field, value);
  }

  // Add a blank template locally (will be created on save)
  function addBlankTemplate() {
    const newTemplate = templateEditing.addPendingTemplate({
      name: "New chart",
      base_type: null,
      chart_type: null,
      source_slide_index: null,
      source_chart_index: null,
      chart_xml: null,
      chart_rels: {},
      style_config: {},
    });
    return newTemplate;
  }

  // Set source chart data on a template (populates all chart fields at once)
  function setSourceChart(templateId, chart) {
    updateLocalTemplate(templateId, 'source_slide_index', chart.slide_index);
    updateLocalTemplate(templateId, 'source_chart_index', chart.chart_index);
    updateLocalTemplate(templateId, 'base_type', chart.base_type);
    updateLocalTemplate(templateId, 'chart_type', chart.base_type?.toLowerCase() || null);
    updateLocalTemplate(templateId, 'chart_xml', chart.chart_xml);
    updateLocalTemplate(templateId, 'chart_rels', chart.chart_rels || {});
    updateLocalTemplate(templateId, 'style_config', chart.style_config || {});
  }

  // Duplicate an existing template (creates a local copy, inserted just after original)
  function duplicateTemplate(templateId) {
    const original = chartTemplates.value.find((t) => t.id === templateId);
    if (!original) return null;

    const newTemplate = templateEditing.addPendingTemplate({
      name: `${original.name} (Copy)`,
      base_type: original.base_type,
      chart_type: original.chart_type,
      report_theme_palette: original.report_theme_palette,
      source_slide_index: original.source_slide_index,
      source_chart_index: original.source_chart_index,
      chart_xml: original.chart_xml,
      chart_rels: { ...(original.chart_rels || {}) },
      style_config: { ...(original.style_config || {}) },
      _insertAfter: templateId,
    });

    return newTemplate;
  }

  // Mark a template for deletion (will be deleted on save)
  function removeTemplate(templateId) {
    templateEditing.removeTemplate(templateId, currentChartTemplateId, () => chartTemplates.value);
  }

  // Reorder templates (after drag-drop)
  function reorderTemplates(orderedIds) {
    templateEditing.reorderTemplates(orderedIds);
  }

  // Clear edit state (when leaving the page)
  function clearEditState() {
    currentChartTemplateId.value = null;
    allCharts.value = [];
    templateEditing.clearEditState();
  }

  // ============================================================================
  // ACTIONS - SAVE
  // ============================================================================

  async function createTemplateOnServer(templateData) {
    // Calculate position if this is a duplicate (inserted after another template)
    let position = null;
    if (templateData._insertAfter) {
      const originalTemplate = serverTemplates.value.find((t) => t.id === templateData._insertAfter);
      if (originalTemplate?.position != null) {
        position = originalTemplate.position + 1;
      }
    }

    const result = await api.createTemplate(templateData, position);
    if (!result.success) throw new Error(result.error);
    return result.template;
  }

  async function updateTemplateOnServer(id, changes) {
    const result = await api.updateTemplate(id, changes);
    if (!result.success) throw new Error(result.error);
    return result.template;
  }

  async function deleteTemplateOnServer(id) {
    const result = await api.deleteTemplate(id);
    if (!result.success) throw new Error(result.error);
    return true;
  }

  async function saveAllChanges() {
    error.value = null;

    try {
      const success = await templateEditing.saveAllChanges(
        {
          createOnServer: createTemplateOnServer,
          updateOnServer: updateTemplateOnServer,
          deleteOnServer: deleteTemplateOnServer,
          refetchTemplates: async () => {
            const fetchResult = await api.fetchTemplates();
            if (fetchResult.success) {
              serverTemplates.value = fetchResult.templates;
            }
          },
        },
        currentChartTemplateId,
      );

      if (success) captureEvent("chart_templates_saved");

      return success;
    } catch (e) {
      error.value = e.message || "Failed to save changes";
      return false;
    }
  }

  // ============================================================================
  // RESET
  // ============================================================================

  function $reset() {
    clearEditState();
    serverTemplates.value = [];
    baseTypes.value = [];
    loading.value = false;
    error.value = null;
    currentThemeToken.value = null;
    templateEditing.$reset();
  }

  return {
    // State
    chartTemplates,
    baseTypes,
    loading,
    error,
    currentThemeToken,
    allCharts,
    currentChartTemplateId,
    currentChartTemplate,
    hasChanges,
    saving: templateEditing.saving,

    // Actions - fetch
    fetchTemplates,
    fetchBaseTypes,
    loadAllCharts,

    // Actions - local edit
    selectTemplate,
    updateLocalTemplate,
    addBlankTemplate,
    setSourceChart,
    duplicateTemplate,
    removeTemplate,
    reorderTemplates,
    getEffectiveTemplateData,
    clearEditState,

    // Actions - save
    saveAllChanges,

    // Utils
    setThemeToken,
    getCompatibleBaseTypes,
    isCrosstabCompatible,
    $reset,
  };
});
